import classNames from 'classnames';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Button from '../../UI/Button';
import InputLabel from '../../UI/InputLabel';
import Pane from '../../UI/Pane';
import '../../UI/TextInput/styles.css';
import './styles.css';
import RawButton from '../../UI/RawButton';
import AsyncCardsSelect from '../AsyncCardsSelect';

const AsyncSelectPane = ({
  selected,
  onChange,
  required,
  title,
  label,
  loading,
  source,
  createNewTitle,
  createNewSubtitle,
  createNewHelp,
  createNewPlaceholder,
  notFoundText,
  searchPlaceholder,
  creatable,
  multiple,
  groupSelect = false,
  help,
  showIcon,
  defaultOption,
  defaultOptionText,
  disabled,
  autoselect,
  disclaimer,
}) => {
  const [showPane, setShowPane] = useState(false);
  const [tempValue, setTempValue] = useState(selected);
  const initialValue = useRef(selected);

  const inputValue = useMemo(() => {
    if (disabled) {
      return '';
    }

    let v = selected;

    if (!multiple && selected) {
      v = [selected];
    }

    return v && v.length > 0 ? v.map(s => s.shortName || s.name).join(', ') : '';
  }, [selected, disabled]);

  const onCloseSchoolsPane = useCallback((closed) => {
    setShowPane(closed);
  }, [selected]);

  const onSelect = useCallback((v, isNew) => {
    onChange(v);

    if (autoselect && !isNew) {
      setShowPane(false);
    }
  }, [autoselect, onChange]);

  const open = useCallback(() => {
    setShowPane(true);
    initialValue.current = selected;
  }, [selected, disabled]);

  const onCreateNewPressEnter = useCallback(() => {
    setShowPane(false);
  }, []);

  useEffect(() => {
    setTempValue(selected);
  }, [selected]);

  return (
    <>
      <div className={classNames('text-input text-input--size-m select-pane-input', {
        'text-input--disabled': disabled === true,
        'text-input--required': required === true,
        'text-input--multiple': multiple === true,
      })}>
        <InputLabel onClick={open} label={label} help={help}>
          <div className={'select-pane-input__inner'}>
            <div className={'text-input__inner text-input__readonly'}>

              {!!selected && !!selected.logo && (
                <div className={'select-pane-input__icon'}>
                  <img width={22} height={22} src={selected.logo} alt={selected.name} />
                </div>
              )}
              <input
                className={'text-input__input'}
                onFocus={(e) => e.target.blur()}
                tabIndex={-1}
                required={required}
                value={inputValue}
                onChange={() => {}}
                disabled={disabled}
              />
              {multiple && (
                <div className={'text-input__values'}>
                  {selected && selected.length > 0 && selected.map((s) => (
                    <div key={s.value} className={'text-input__value'}>
                      {s.name}
                      <button type={'button'} onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onChange(selected.filter((v) => v.value !== s.value));
                      }} className={'text-input__value-delete'}>&times;</button>
                    </div>
                  ))}
                </div>
              )}
              {(loading) && (
                <div className={'text-input__loader'} />
              )}
            </div>
            <button
              className={'select-pane-input__button'}
              type={'button'}
              onClick={open}
            >
              Change
            </button>
          </div>
        </InputLabel>
      </div>
      <Pane
        show={showPane}
        onClose={onCloseSchoolsPane}
        footer={autoselect ? undefined : (
          <>
            <Button
              onClick={() => {
                setTempValue(initialValue.current);
                onChange(initialValue.current);
                setShowPane(false);
              }}
              secondary
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowPane(false);
              }}
            >
              Confirm
            </Button>
          </>
        )}
      >
        {!!title && (
          <h2 className={'pane__title'}>{title}</h2>
        )}
        {!!help && (
          <div className={'pane__subtitle'}>
            {help}
          </div>
        )}
        <AsyncCardsSelect
          selected={tempValue}
          onChange={onSelect}
          source={source}
          createNewTitle={createNewTitle}
          createNewSubtitle={createNewSubtitle}
          createNewHelp={createNewHelp}
          createNewPlaceholder={createNewPlaceholder}
          notFoundText={notFoundText}
          searchPlaceholder={searchPlaceholder}
          creatable={creatable}
          multiple={multiple}
          groupSelect={groupSelect}
          showIcon={showIcon}
          onCreateNewPressEnter={onCreateNewPressEnter}
          autoselect={autoselect}
          disclaimer={disclaimer}
        />
        {!!defaultOption && (
          <div className={'pane__help'}>
            <RawButton
              onClick={() => {
                if (defaultOption) {
                  onChange(defaultOption);
                  setShowPane(false);
                }
              }}
            >
              {defaultOptionText || 'Select default'}
            </RawButton>
          </div>
        )}
      </Pane>
    </>
  );
};

export default AsyncSelectPane;
